import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api/client";
import type { Client, CronJob, CronRun, GatewayAccount, Instance, InstanceLogsState, MiniInstallation, NoticeType, QueueItem, ServerDraft, ServerTarget } from "@/types";
import { createEmptyServerDraft, inferNoticeType } from "@/lib/formatters";
import { useAppNavigation } from "@/modules/panel/hooks/useAppNavigation";
import { useAppData } from "@/modules/panel/hooks/useAppData";
import { useClientActions } from "@/modules/clients/hooks/useClientActions";
import { useGatewayActions } from "@/modules/gateway/hooks/useGatewayActions";
import { useDeliveryActions } from "@/modules/delivery/hooks/useDeliveryActions";
import { useServerActions } from "@/modules/servers/hooks/useServerActions";
import { useMiniActions } from "@/modules/installations/hooks/useMiniActions";
import { usePanelDialogs } from "@/modules/panel/hooks/usePanelDialogs";
import type { PanelControllerOptions } from "@/modules/panel/types";

const initialClients: Client[] = [];
const initialInstances: Instance[] = [];
const initialQueue: QueueItem[] = [];

export function usePanelController({ isAuthenticated, authLogout }: PanelControllerOptions) {
  const { page, changePage, sidebarCollapsed, setSidebarCollapsed } = useAppNavigation();
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [clients, setClients] = useState(initialClients);
  const [instances, setInstances] = useState(initialInstances);
  const [queue, setQueue] = useState(initialQueue);
  const [query, setQuery] = useState("");
  const [selectedClientId, setSelectedClientId] = useState("");
  const [licenseDialogOpen, setLicenseDialogOpen] = useState(false);
  const [installStep, setInstallStep] = useState(3);
  const [localApiOnline, setLocalApiOnline] = useState(false);
  const [installations, setInstallations] = useState<MiniInstallation[]>([]);
  const [servers, setServers] = useState<ServerTarget[]>([]);
  const [gatewayAccounts, setGatewayAccounts] = useState<GatewayAccount[]>([]);
  const [cronJobs, setCronJobs] = useState<CronJob[]>([]);
  const [cronRuns, setCronRuns] = useState<CronRun[]>([]);
  const [gatewayAccountName, setGatewayAccountName] = useState("");
  const [deliveryDraft, setDeliveryDraft] = useState({ clientId: "", clientName: "", aiAccountId: "", telegramBotToken: "", telegramOwnerId: "", miniName: "", memoryMb: "1024", cpuCores: "1", serverId: "" });
  const [deliveryResult, setDeliveryResult] = useState<{ mode?: string; instance?: MiniInstallation; license?: any; installCommand?: string; package?: { notes?: string } } | null>(null);
  const [deliverySubmitting, setDeliverySubmitting] = useState(false);
  const [instanceLogs, setInstanceLogs] = useState<InstanceLogsState | null>(null);
  const [serverDraft, setServerDraft] = useState<ServerDraft>(() => createEmptyServerDraft());
  const [newClient, setNewClient] = useState({ name: "", owner: "", licenseMode: "lease" as "lease" | "perpetual", leaseDays: "30" });
  const [miniSource, setMiniSource] = useState<any>(null);
  const [miniSourceSyncing, setMiniSourceSyncing] = useState(false);
  const [miniSourceChecking, setMiniSourceChecking] = useState(false);
  const [gatewayLoginPending, setGatewayLoginPending] = useState(false);
  const [gatewayCheckingIds, setGatewayCheckingIds] = useState<Set<string>>(() => new Set());
  const refreshInFlight = useRef(false);
  const gatewayEnabled = true;

  const notify = (text: string, type?: NoticeType) => {
    const inferredType = type || inferNoticeType(text);
    if (inferredType === "error") toast.error(text);
    else toast.success(text);
  };

  const dialogs = usePanelDialogs(notify);
  const selectedClient = clients.find((client) => client.id === selectedClientId) ?? null;
  const logout = async () => {
    await authLogout();
    setClients([]); setInstances([]); setInstallations([]);
  };

  const navigateFromSidebar = (nextPage: any) => {
    if (nextPage === "delivery") {
      setSelectedClientId("");
      setDeliveryDraft((draft) => ({ ...draft, clientId: "", clientName: "", aiAccountId: "", serverId: "" }));
    }
    changePage(nextPage);
  };

  const openDeliveryForClient = (clientId = selectedClient?.id || "") => {
    const client = clients.find((item) => item.id === clientId) || selectedClient;
    if (client) {
      setSelectedClientId(client.id);
      setDeliveryDraft((draft) => ({ ...draft, clientId: client.id, clientName: client.name }));
    }
    changePage("delivery");
  };

  const stats = useMemo(() => {
    const active = clients.filter((client) => client.status === "active").length;
    const warning = clients.filter((client) => client.status === "warning").length;
    const blocked = clients.filter((client) => client.status === "blocked").length;
    const totalSpend = 0;
    const online = instances.filter((instance) => instance.status === "online").length;
    const tokens = 0;
    return { active, warning, blocked, totalSpend, online, tokens };
  }, [clients, instances]);

  const refreshData = useAppData(isAuthenticated, { setLoading: setIsDataLoading, setApiOnline: setLocalApiOnline, setMiniSource, setInstallations, setClients, setInstances, setSelectedClientId, setServers, setCronJobs, setCronRuns, setGatewayAccounts, setProxyUrl: dialogs.setSettingsProxyUrl }, refreshInFlight);
  const clientActions = useClientActions({ clients, localApiOnline, refreshData, notify, setSelectedClientId, setLicenseDialogOpen, setNewClient, newClient });
  const gatewayActions = useGatewayActions({ localApiOnline, gatewayAccountName, setGatewayAccountName, setGatewayAccounts, setGatewayLoginPending, setGatewayCheckingIds, notify });
  const deliveryActions = useDeliveryActions({ deliveryDraft, clients, selectedClient, gatewayAccounts, localApiOnline, deliverySubmitting, setDeliverySubmitting, setDeliveryResult, refreshData, notify });
  const serverActions = useServerActions({ localApiOnline, serverDraft, setServerDraft, setAgentInstallCommand: (command) => dialogs.setAgentInstallCommand(command ? { serverId: "", command } : null), refreshData, notify });
  const miniActions = useMiniActions({ installations, localApiOnline, refreshData, notify, waitForHostCommand: serverActions.waitForHostCommand, setMiniSource, setMiniSourceSyncing, setMiniSourceChecking, setInstanceLogs });

  const copyGatewayValue = async (value: string, message: string) => {
    try { await navigator.clipboard.writeText(value); notify(message); } catch { notify(value); }
  };
  const saveCronJob = async (job: Partial<CronJob>) => {
    const response = await apiFetch("/api/cron/jobs", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(job) });
    const payload = await response.json();
    if (!response.ok || !payload.ok) throw new Error(payload.error || `HTTP ${response.status}`);
    await refreshData(); notify("Событие расписания сохранено");
  };
  const deleteCronJob = async (id: string) => {
    const response = await apiFetch(`/api/cron/jobs/${encodeURIComponent(id)}/delete`, { method: "POST" });
    const payload = await response.json();
    if (!response.ok || !payload.ok) throw new Error(payload.error || `HTTP ${response.status}`);
    await refreshData(); notify("Событие удалено");
  };
  const runMiniAction = (task: string) => {
    setQueue((items) => [{ id: `job_${Math.random().toString(16).slice(2, 8)}`, task, status: "В работе", duration: "0.0 сек" }, ...items]);
    notify(`${task}: задача добавлена`);
  };

  const model = {
    page, clients, instances, stats, selectedClient, openDeliveryForClient, changePage, isDataLoading, refreshData, deliveryDraft, deliveryResult, gatewayAccounts, servers, localApiOnline, deliverySubmitting, setDeliveryDraft, setSelectedClientId, issueDelivery: deliveryActions.issueDelivery, query, licenseDialogOpen, newClient, setQuery, setLicenseDialogOpen, setNewClient, createClient: clientActions.createClient, updateClientProfile: clientActions.updateClientProfile, renewLicense: clientActions.renewLicense, deleteClientLicense: clientActions.deleteClientLicense, licenseAdminAction: clientActions.licenseAdminAction, installations, miniSource, instanceApiAction: miniActions.instanceApiAction, controlCommand: miniActions.controlCommand, sendMiniMessage: miniActions.sendMiniMessage, sendOwnerMessage: miniActions.sendOwnerMessage, updateAllMini: miniActions.updateAllMini, updateInstanceSettings: miniActions.updateInstanceSettings, updateClientAIAccount: clientActions.updateClientAIAccount, loadInstanceLogs: miniActions.loadInstanceLogs, instanceLogs, cronJobs, cronRuns, saveCronJob, deleteCronJob, serverDraft, setServerDraft, saveServerTarget: serverActions.saveServerTarget, editServerTarget: serverActions.editServerTarget, resetServerDraft: serverActions.resetServerDraft, serverApiAction: serverActions.serverApiAction, requestAgentInstall: serverActions.requestAgentInstall, gatewayAccountName, gatewayLoginPending, setGatewayAccountName, connectGatewayAccount: gatewayActions.connectGatewayAccount, reconnectGatewayAccount: gatewayActions.reconnectGatewayAccount, refreshGatewayAccountStatus: gatewayActions.refreshGatewayAccountStatus, refreshAllGatewayAccountStatus: gatewayActions.refreshAllGatewayAccountStatus, deleteGatewayAccount: gatewayActions.deleteGatewayAccount, downloadGatewayAccountAuth: gatewayActions.downloadGatewayAccountAuth, copyGatewayValue, gatewayCheckingIds, installStep, queue, miniSourceSyncing, miniSourceChecking, setInstallStep, runMiniAction, checkMiniSource: miniActions.checkMiniSource, syncMiniSource: miniActions.syncMiniSource, gatewayEnabled,
  };

  return { ...model, ...dialogs, sidebarCollapsed, setSidebarCollapsed, navigateFromSidebar, logout };
}
